import React, { useState, useMemo } from "react";
import { Typography, Grid, Button } from "@mui/material/";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { groupBy, some } from "lodash";

import { strings } from "../../../../../common/i18n";
import { enums, format, helpers } from "../../../../../common/lib";
import actions from "../../../../../Store/actions";
import {
    InfoThemeProvider,
    ErrorThemeProvider,
    SuccessThemeProvider
} from "../../../../../common/styles/providers";
import {
    useAssetTypesForOrganisation,
    useMakesForAssetTypes,
    useAuditStatus,
    useExclusionDatePicker
} from "../../../../../common/hooks";
import {
    checkStatuses,
    customBooleanFilterOptions,
    getIdentifiers,
    renderCirlce
} from "./tableHelper";
import useUnexpectedAssetColsRows from "./useUnexpectedAssetColsRows";
import useExpectedAssetColsRows from "./useExpectedAssetColsRows";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import styles from "./styles";
import ShareIcon from "@mui/icons-material/Share";

/**
 *  Hook for user table data and operations
 */
export default function useColumnsRows() {
    const {
        expectedLoading,
        setExpectedLoading,
        expectedColumns,
        expectedData,
        expectedRowActions,
        expectedRowClassName,
        rejectDialogOpen,
        excludeDialogOpen,
        handleExpectedEdit,
        handleExpectedChangeState,
        onExcludeDialogOpen,
        onExcludeDialogClose,
        moveAssetDialogOpen,
        onMoveAssetDialogClose,
        onRejectDialogHide,
        onRejectDialogShow,
        onRejectSubmit,
        expectedToolBar,
        expectedAssetSelection
    } = useExpectedAssetColsRows();
    const {
        unexpectedLoading,
        setUnexpectedLoading,
        unexpectedColumns,
        unexpectedData,
        unexpectedRowActions,
        unexpectedSelectedAssets,
        unexpectedRowClassName,
        handleUnexpectedEdit,
        resolveDialogOpen,
        handleUnexpectedChangeState,
        onResolveDialogOpen,
        onResolveDialogClose,
        unexpectedToolBar,
        currentRow,
        assetMatchDrawerOpen,
        setAssetMatchDrawerOpen,
        suggestedMatchDialog,
        setSuggestedMatchDialog
    } = useUnexpectedAssetColsRows();

    const { auditReview } = useSelector((state) => state);

    const condition = checkStatuses(auditReview.status);

    const useColsRows = useMemo(() => {
        let useColsRows;
        switch (condition) {
            case "expected":
            case "validatedVisibility":
            case "nonMandatory":
                useColsRows = {
                    loading: expectedLoading,
                    setLoading: setExpectedLoading,
                    columns: expectedColumns ?? [],
                    data: expectedData ?? [],
                    getRowClassName: expectedRowClassName,
                    onEdit: handleExpectedEdit,
                    onChangeState: handleExpectedChangeState,
                    selectedAssets: expectedAssetSelection,
                    toolBarComponents: expectedToolBar,
                    rowActions: expectedRowActions,
                    excludeDialogOpen,
                    rejectDialogOpen,
                    moveAssetDialogOpen,
                    onMoveAssetDialogClose,
                    onExcludeDialogClose,
                    onRejectDialogHide,
                    onRejectSubmit
                };
                break;
            case "unexpected":
                useColsRows = {
                    loading: unexpectedLoading,
                    setLoading: setUnexpectedLoading,
                    columns: unexpectedColumns ?? [],
                    data: unexpectedData ?? [],
                    rowActions: unexpectedRowActions,
                    getRowClassName: unexpectedRowClassName,
                    onEdit: handleUnexpectedEdit,
                    onChangeState: handleUnexpectedChangeState,
                    selectedAssets: unexpectedSelectedAssets,
                    toolBarComponents: unexpectedToolBar,
                    resolveDialogOpen,
                    onResolveDialogOpen,
                    onResolveDialogClose,
                    currentRow,
                    assetMatchDrawerOpen,
                    setAssetMatchDrawerOpen
                };
                break;

            default:
                useColsRows = {
                    loading: true,
                    setLoading: () => {},
                    columns: [],
                    data: [],
                    getRowClassName: () => "",
                    onEdit: () => {},
                    onChangeState: () => {},
                    toolBarComponents: null
                };
        }
        return useColsRows;
    }, [
        condition,
        expectedLoading,
        setExpectedLoading,
        expectedColumns,
        expectedData,
        expectedRowClassName,
        handleExpectedEdit,
        handleExpectedChangeState,
        expectedToolBar,
        unexpectedLoading,
        setUnexpectedLoading,
        unexpectedColumns,
        unexpectedData,
        unexpectedRowClassName,
        handleUnexpectedEdit,
        handleUnexpectedChangeState,
        unexpectedToolBar
    ]);

    return useColsRows;
}
